


import React, { useEffect, useState } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import Swal from "sweetalert2";


/*
  Robust BedDetails with aggressive fallbacks and helpful console logs.
  - Tries many field names for UHID/name
  - Calls getBedListUrl as fallback to show free beds
  - Optionally calls AppConst.getIpdByIdUrl if available to fetch patient details by id
  - Adds verbose console logs so you can paste a sample object if still failing
*/

const BedDetails = () => {
  const [formData, setFormData] = useState({ l1: [], wards: [], page_no: 0 });
  const [showModal, setShowModal] = useState(false);
  const [modalState, setModalState] = useState({
    currentBed: null,
    patientUhid: "",
    patientName: "",
    patientId: "",
    currentAllocationStart: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTargetBed, setSelectedTargetBed] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [loadingIpd, setLoadingIpd] = useState(true);
  // in initData2: setLoadingIpd(false) at end
  // and in render: {!loadingIpd ? displayPatientNameForBed(b) : 'Loading...'}
  
  // initial load of wards & ipd
  const initData = async () => {
    try {
      const fd = new FormData();
      fd.append("par_by", "1");
      const resp = await axios.post(AppConst.BASE_URL + AppConst.getBedListUrl1, fd);
      if (resp.data?.status) setFormData((p) => ({ ...p, wards: resp.data.records || [] }));
    } catch (e) {
      console.error("initData getBedListUrl1 error", e);
    }
  };

  const initData2 = async () => {
    setLoadingIpd(true);
    try {
      const fd = new FormData();
      fd.append("page_no", "0");
      fd.append("search_key", "");
      //fd.append("ipd_user", "1");
      const resp = await axios.post(AppConst.BASE_URL + AppConst.getIpdListUrl, fd);
  
      // DEBUG: always log what the backend returned (paste sample here if still failing)
      console.debug("initData2 response:", resp?.data);
  
      if (resp.data?.status && Array.isArray(resp.data.records)) {
        console.debug("initData2 - records[0]:", resp.data.records[0]);
        setFormData((p) => ({ ...p, l1: resp.data.records || [] }));
      } else {
        console.warn("initData2 - no records or status=false", resp?.data);
        setFormData((p) => ({ ...p, l1: [] }));
      }
    } catch (e) {
      console.error("initData2 getIpdListUrl error", e);
      setFormData((p) => ({ ...p, l1: [] }));
    } finally {
      setLoadingIpd(false);
    }
  };
  

  useEffect(() => {
    const stored = sessionStorage.getItem("ldata");
    if (stored) {
      initData();
      initData2();
    }
    // eslint-disable-next-line
  }, []);

  // --- Helpers to extract fields robustly ---
  const nameCandidates = (rec) =>
    rec?.lab_reg_name ||
    rec?.ipd_patient_name ||
    rec?.patient_name ||
    rec?.name ||
    rec?.fname ||
    rec?.opd_patient_name ||
    "";
// returns ipd record for a bed user id (tries multiple id fields)
// returns ipd/reg record for a bed user id (tries many likely id fields)
const findIpdByUserId = (userId) => {
  if (!userId) return null;
  const uid = String(userId).trim();
  const list = Array.isArray(formData.l1) ? formData.l1 : [];

  const idFields = (rec) => [
    rec?.ipd_id, rec?.id, rec?.user_id, rec?.lab_reg_id,
    rec?.opd_id, rec?.patient_id, rec?.ipd_patient_id, rec?.reg_id,
    rec?.patient_uid, rec?.uid
  ].filter(x => x !== undefined && x !== null).map(x => String(x).trim());

  // exact match
  const exact = list.find(rec => idFields(rec).some(id => id === uid));
  if (exact) return exact;

  // tolerant match: compare numeric values too (e.g. "72" vs 72)
  const numeric = Number(uid);
  if (!isNaN(numeric)) {
    const numMatch = list.find(rec => idFields(rec).some(id => !isNaN(Number(id)) && Number(id) === numeric));
    if (numMatch) return numMatch;
  }

  // fuzzy: match UHID or phone fields
  const fuzzy = list.find(rec => {
    const recUhid = (rec?.ipd_uhid || rec?.uhid || rec?.lab_reg_uhid || '').toString().trim();
    const phone = (rec?.ipd_patient_mobile || rec?.mobile || rec?.patient_mobile || rec?.phone || '').toString().trim();
    return recUhid === uid || phone === uid;
  });
  if (fuzzy) return fuzzy;

  // FINAL: if you have an API to fetch IPD by id, try it (optional)
  // This is synchronous function — but we can mark 'not found' and optionally fetch on openTransferModal instead.
  return null;
};
// return best available UHID for a record or bed object
const displayUhidForRecord = (rec) => {
  if (!rec) return '';
  return (
    rec.lab_reg_uhid ||
    rec.ipd_uhid ||
    rec.opd_uhid_move ||
    rec.uhid ||
    rec.uhid_no ||
    rec.uhidNumber ||
    ''
  );
};

// combined helper that returns { name, uhid } for a bed (tries linked ipd record, then bed)
const getPatientInfoForBed = (bed) => {
  const rec = findIpdByUserId(bed.user_id || bed.user || bed.ipd_id || bed.patient_id) || {};
  const name =
    rec.ipd_patient_name ||
    rec.patient_name ||
    rec.lab_reg_name ||
    rec.name ||
    rec.fname ||
    rec.full_name ||
    rec.opd_patient_name ||
    bed.ipd_patient_name ||
    bed.patient_name ||
    '';
  const uhid = displayUhidForRecord(rec) || displayUhidForRecord(bed) || '';
  return { name: name || '', uhid: uhid || '' };
};

// Compose/display the patient name for a bed
const displayPatientNameForBed = (bed) => {
  if (!bed) return "";

  // try to resolve using bed.user_id (most common)
  const rec = findIpdByUserId(bed.user_id || bed.user || bed.ipd_id || bed.patient_id);
  if (rec) {
    // try many name fields
    return (
      rec.ipd_patient_name ||
      rec.patient_name ||
      rec.lab_reg_name ||
      rec.name ||
      rec.fname ||
      rec.full_name ||
      rec.opd_patient_name ||
      rec.patient_fname ||
      ""
    );
  }

  // final fallback: some bed objects contain patient info inline
  return (
    bed.ipd_patient_name ||
    bed.patient_name ||
    bed.lab_reg_name ||
    bed.name ||
    ""
  );
};


  const uhidCandidates = (rec) =>
    rec?.lab_reg_uhid ||
    rec?.ipd_uhid ||
    rec?.opd_uhid_move ||
    rec?.uhid ||
    rec?.uhid_no ||
    rec?.uhidNumber ||
    "";

  const idCandidates = (rec) =>
    rec?.ipd_id || rec?.user_id || rec?.id || rec?.lab_reg_id || "";

  const isBedFree = (b) => {
    if (!b) return false;
    const s = (b.status !== undefined && b.status !== null) ? String(b.status).toLowerCase() : "";
    if (["0", "free", "available", "false", ""].includes(s)) return true;
    if (b.is_free === true || b.is_free === "1" || b.is_free === 1) return true;
    if (b.user_id === "0" || b.user_id === 0 || b.user_id === null || b.user_id === "") return true;
    return false;
  };

  const flattenWardsToBeds = (wards) => {
    const out = [];
    (wards || []).forEach((w) => {
      const catName = w.cat_name || w.ward_name || "";
      (w.beds || []).forEach((b) => out.push({ ...b, cat_name: catName }));
    });
    return out;
  };

  // Fetch available beds (try search API, then getBedListUrl, then local)
  const populateAvailableBeds = async (q) => {
    // 1) try searchBedsUrl
    if (AppConst.searchBedsUrl) {
      try {
        const fd = new FormData();
        fd.append("search_key", q || "");
        const resp = await axios.post(AppConst.BASE_URL + AppConst.searchBedsUrl, fd);
        if (resp.data?.status) {
          const recs = (resp.data.records || []).filter((b) => isBedFree(b));
          setSearchResults(recs);
          return;
        }
      } catch (e) {
        console.warn("searchBedsUrl failed, falling back", e);
      }
    }

    // 2) try getBedListUrl (same used in your registration)
    if (AppConst.getBedListUrl) {
      try {
        const fd = new FormData();
        fd.append("page_no", "none");
        fd.append("bed_user", "1");
        if (q) fd.append("search_key", q);
        const resp = await axios.post(AppConst.BASE_URL + AppConst.getBedListUrl, fd);
        if (resp.data?.status) {
          const recs = (resp.data.records || []).filter((b) => isBedFree(b));
          const filtered = q ? recs.filter(r => String(r.bed_name || "").toLowerCase().includes(q.toLowerCase()) || String(r.cat_name || r.ward_name || "").toLowerCase().includes(q.toLowerCase())) : recs;
          setSearchResults(filtered);
          return;
        }
      } catch (e) {
        console.warn("getBedListUrl fallback failed", e);
      }
    }

    // 3) final fallback: local wards data already in memory
    const available = flattenWardsToBeds(formData.wards).filter((b) => isBedFree(b));
    const final = q ? available.filter(r => String(r.bed_name || "").toLowerCase().includes(q.toLowerCase()) || String(r.cat_name || "").toLowerCase().includes(q.toLowerCase())) : available;
    setSearchResults(final);
  };

  // Open modal: populate patient info and available beds
  const openTransferModal = async (bedItem) => {
    console.log("openTransferModal bedItem:", bedItem);
    const source = bedItem || {};
    // find ipd record from l1 by user id / ipd_id
    const pidCandidates = [source.user_id, source.ipd_id, source.patient_id, source.user || source.id].filter(Boolean);
    let foundIpd = null;
    if (pidCandidates.length && formData.l1 && formData.l1.length) {
      foundIpd = formData.l1.find(rec => pidCandidates.some(p => String(rec.ipd_id) === String(p) || String(rec.user_id) === String(p) || String(rec.id) === String(p)));
    }

    // if not found and you have an API to fetch ipd by id, try it
    let fetchedIpd = null;
    if (!foundIpd && AppConst.getIpdByIdUrl && pidCandidates.length) {
      try {
        const fd = new FormData();
        fd.append("id", pidCandidates[0] + "");
        const resp = await axios.post(AppConst.BASE_URL + AppConst.getIpdByIdUrl, fd);
        if (resp.data?.status) fetchedIpd = resp.data.record;
      } catch (e) {
        console.warn("getIpdByIdUrl failed", e);
      }
    }

    // If nothing found, try sessionStorage.ipd_dt
    let sess = null;
    try { sess = JSON.parse(sessionStorage.getItem("ipd_dt") || "null"); } catch (e) { sess = null; }

    // Compose patient values using many candidates
    // inside BedDetails.js -> openTransferModal(...)
const ipdRec = foundIpd || fetchedIpd || sess || null;
const patientName = nameCandidates(ipdRec) || nameCandidates(source) || "";
const patientUhid = uhidCandidates(ipdRec) || uhidCandidates(source) || (sess && (sess.ipd_uhid || sess.lab_reg_uhid || sess.opd_uhid_move)) || "";
const patientId = idCandidates(ipdRec) || pidCandidates[0] || "";

// --- NEW: pick an allocation start from multiple candidate fields ---
const allocationCandidates = [
  ipdRec && (ipdRec.bed_allocated_at || ipdRec.allocated_at || ipdRec.ipd_admit_date || ipdRec.created_at || ipdRec.admit_datetime),
  source.bed_datetime || source.allocated_at || source.created_at || source.assigned_at,
  sess && (sess.bed_datetime || sess.created_at)
].filter(Boolean);

const allocationStart = allocationCandidates.length ? allocationCandidates[0] : null;

// debug so you can confirm value in console
console.log("allocationCandidates:", allocationCandidates, "allocationStart:", allocationStart);

// set modal state including allocation start
setModalState({
  currentBed: source,
  patientName,
  patientUhid,
  patientId,
  currentAllocationStart: allocationStart,
});


    await populateAvailableBeds("");
    setSelectedTargetBed(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalState({ currentBed: null, patientUhid: "", patientName: "", patientId: "", currentAllocationStart: null });
    setSearchResults([]);
    setSelectedTargetBed(null);
  };

  const handleSelectTargetBed = (b) => {
    if (!isBedFree(b)) {
      Swal.fire("Bed not free", "Please pick an available bed.", "warning");
      return;
    }
    setSelectedTargetBed(b);
  };

  const computeMinutesOccupied = (start) => {
    if (!start) return 0;
    const dt = new Date(start);
    if (isNaN(dt.getTime())) return 0;
    return Math.floor((new Date() - dt) / (1000 * 60));
  };

  const doTransfer = async (targetBed) => {
    if (!modalState.currentBed) return Swal.fire("No source", "No source bed selected", "error");
    if (!modalState.patientId) return Swal.fire("No patient id", "Cannot find patient id for the source bed. Check console output.", "error");
    if (!targetBed) return Swal.fire("No target", "Please select a target bed", "error");
    if (!AppConst.transferBedUrl) return Swal.fire("Config", "Please set AppConst.transferBedUrl in frontend.", "error");

    const minutes = computeMinutesOccupied(modalState.currentAllocationStart);
    const fd = new FormData();
    fd.append("patient_id", modalState.patientId + "");
    fd.append("uhid", modalState.patientUhid + "");
    fd.append("source_bed_id", modalState.currentBed.bed_id || modalState.currentBed.id || "");
    fd.append("target_bed_id", targetBed.bed_id || targetBed.id || "");
    fd.append("minutes_occupied", String(minutes));
    fd.append("by_user", "1");

    try {
      const confirm = await Swal.fire({
        title: "Confirm transfer",
        html: `<p>From <b>${modalState.currentBed?.bed_name}</b> → To <b>${targetBed?.bed_name}</b></p>`,
        showCancelButton: true,
        confirmButtonText: "Transfer",
      });
      if (!confirm.isConfirmed) return;

      setTransferLoading(true);
      const resp = await axios.post(AppConst.BASE_URL + AppConst.transferBedUrl, fd);
      if (resp.data?.status) {
        await initData();
        await initData2();
        closeModal();
        Swal.fire("Done", resp.data.message || "Transfer successful", "success");
      } else {
        console.error("Transfer server response:", resp.data);
        Swal.fire("Failed", resp.data?.message || "Server rejected transfer", "error");
      }
    } catch (e) {
      console.error("Transfer API error:", e);
      Swal.fire("Error", "Network/server error (see console log)", "error");
    } finally {
      setTransferLoading(false);
    }
  };

  // UI render
  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card" id="orderList">
          <div className="card-header">
            <h5 className="card-title mb-0">Bed List</h5>
          </div>
          <div className="card-body pt-0">
            {(formData.wards || []).map((w, wi) => (
              (w.beds || []).length > 0 ? (
                <div className="row" key={wi}>
                  <div className="col-md-5" />
                  <div className="col-md-2 text-center">
                    <div className="alert alert-dark" style={{ borderRadius: "1rem" }}>
                      <strong style={{ fontSize: "1rem" }}>{w.cat_name}</strong>
                    </div>
                  </div>
                  <div className="col-md-5" />
                  {(w.beds || []).map((b, bi) => (
                    <div className="col-md-3" key={bi}>
                      <div className="card card-animate">
                        <div className="card-body">
                          {!isBedFree(b) && (
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Patient:</p>
                              </div>
                              <div className="flex-shrink-0">
                              {(() => {
  const info = getPatientInfoForBed(b);
  return (
    <>
      <h5 className="text-dark fs-14 mb-0">{info.name || '—'}</h5>
      {info.uhid ? <div style={{ fontSize: 12, color: '#6c757d' }}>UHID: {info.uhid}</div> : null}
    </>
  );
})()}


                              </div>
                            </div>
                          )}
                          <div className="d-flex align-items-end justify-content-between mt-4">
                            <div>
                              <h4 className="fs-22 fw-semibold ff-secondary mb-4">{b.bed_name}</h4>
                              <a className="text-decoration-underline">Price: {b.bed_price}</a>
                            </div>
                            <div className="avatar-sm flex-shrink-0">
                              <span className={`bed-icon ${isBedFree(b) ? "bed-available" : "bed-allocated"}`}>
                                <i className="bx bx-bed" />
                              </span>
                            </div>
                          </div>
                        </div>

                        {!isBedFree(b) && (
                          <div className="btn btn-info text-bg-light transfer" style={{ cursor: "pointer" }} onClick={() => openTransferModal(b)}>
                            Transfer
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null
            ))}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Transfer Bed</h5>
                <button className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label>UHID</label>
                    <input className="form-control" value={modalState.patientUhid} readOnly />
                  </div>
                  <div className="col-md-6">
                    <label>Patient Name</label>
                    <input className="form-control" value={modalState.patientName} readOnly />
                  </div>
                </div>

                <div className="mt-3">
                  <label>Search Beds (free beds)</label>
                  <input className="form-control mb-2" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); populateAvailableBeds(e.target.value); }} placeholder="Search bed name / ward" />
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {searchResults.length ? searchResults.map((b, i) => {
                      const sel = selectedTargetBed && (b.bed_id === selectedTargetBed.bed_id || b.id === selectedTargetBed.id);
                      return (
                        <div key={i} className={`card mb-2 ${sel ? "border-primary" : ""}`} style={{ cursor: "pointer" }} onClick={() => handleSelectTargetBed(b)}>
                          <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{b.bed_name}</strong>
                              <div style={{ fontSize: 12 }}>{b.cat_name || b.ward_name || ""}</div>
                            </div>
                            <div>
                              <span className={`badge ${isBedFree(b) ? "bg-success" : "bg-secondary"}`}>{isBedFree(b) ? "Available" : "Occupied"}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }) : <div className="text-muted">No free beds found (check console/network).</div>}
                  </div>
                </div>

                <hr />

                <div>
                  <small>Current bed: <strong>{modalState.currentBed?.bed_name}</strong></small><br />
                  <small>Minutes occupied: {computeMinutesOccupied(modalState.currentAllocationStart)}</small>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>Close</button>
                <button className="btn btn-success" disabled={!selectedTargetBed || transferLoading} onClick={() => doTransfer(selectedTargetBed)}>{transferLoading ? "Transferring..." : "Transfer"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BedDetails;
